import React, { useCallback, useMemo, useState, createContext, useContext } from 'react';
import { translations, languages, type Language, type LanguageOption } from './translations';
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string>) => string;
  languages: LanguageOption[];
}
const LanguageContext = createContext<LanguageContextType | null>(null);
function getInitialLanguage(): Language {
  try {
    const stored = localStorage.getItem('astriqo-lang');
    if (stored && ['ru', 'en', 'zh', 'es'].includes(stored)) {
      return stored as Language;
    }
  } catch {}
  return 'ru';
}
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem('astriqo-lang', lang);
    } catch {}
  }, []);
  const t = useCallback((key: string, params?: Record<string, string>): string => {
    let value = translations[language]?.[key] || translations['ru']?.[key] || key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        value = value.replace(`{${k}}`, v);
      });
    }
    return value;
  }, [language]);
  const value = useMemo(() => ({
    language,
    setLanguage,
    t,
    languages
  }), [language, setLanguage, t]);
  return <LanguageContext.Provider value={value} data-id="element-19498">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}